import { lazy, Suspense, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHead } from "@/components/PageHead";

import { ago, POLL } from "@/lib/format";
import { assetData } from "@/api/client";
import { Donut } from "@/components/Donut";

// lazy: Leaflet is ~145KB and the map is the only thing on any page that
// needs it, so every other route keeps the pre-map bundle (same
// arrangement as the History replay).
const FleetMap = lazy(() => import("@/modules/fleet/FleetMap")
  .then((m) => ({ default: m.FleetMap })));

const VER_COLORS = ["var(--teal)", "var(--info)", "var(--warn)", "var(--t-dim)", "var(--line2)"];


/** Live fleet health — who is reporting right now, on which firmware. Sourced
 *  from device_latest_state (the freshest snapshot per device).
 *
 *  MOVED OUT OF ASSET DATA, 2026-10-23. Dattatray: *"Asset data overview shall
 *  be part of fleet page right? I think asset data shall have Live and Historic
 *  and that should be it."* Right, and the code already said so in three
 *  places: this page is a FLEET view (a map of many devices, a company picker,
 *  a fleet-wide firmware distribution) while Live Window and History are both
 *  keyed on one `imei`; `GATE_FLEET`'s own capability description reads "Live
 *  map and commands" while Fleet had no map; and `FleetMap.tsx` was sitting in
 *  the asset_data folder. Same cut as D-111, which moved Asset Control out.
 *
 *  THE ENDPOINT STAYS AT `/modules/asset-data/overview`. Moving a page is a
 *  console change; moving the route would be a server change plus the
 *  path-contract test for no behavioural gain. The URL is not the owner. */
export function FleetOverviewPage() {
  // Scoped by the SAME company the map is showing, so the counts under the
  // map describe the fleet on it. The endpoint already took `company`; the
  // page simply never passed one, so the picker looked like a page filter and
  // behaved like a map filter.
  const [company, setCompany] = useState("");
  const ov = useQuery({
    queryKey: ["live", "overview", company],
    queryFn: () => assetData.overview(company || undefined),
    refetchInterval: POLL.DASHBOARD });
  const d = ov.data;
  const slices = d?.by_fw ?? [];
  const winMin = d ? Math.round(d.online_window_s / 60) : 5;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Fleet · Overview" title="Devices reporting now" />

      <div className="section-label">Where the fleet is</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <Suspense fallback={<div className="cap" style={{ height: 460 }}>Loading map…</div>}>
          <FleetMap onCompany={setCompany} />
        </Suspense>
      </div>

      <div className="section-label">Fleet liveness</div>
      <div className="livegrid" style={{ marginBottom: 16 }}>
        <div>
          <div className="k">Reporting</div>
          <div className="v num">{(d?.total ?? 0).toLocaleString("en-IN")}</div>
          <div className="s">devices with a snapshot</div>
        </div>
        <div>
          <div className="k">Online now</div>
          <div className="v num" style={{ color: "var(--ok)" }}>{(d?.online ?? 0).toLocaleString("en-IN")}</div>
          <div className="s">seen ≤ {winMin} min</div>
        </div>
        <div>
          <div className="k">Stale</div>
          <div className="v num">{(d?.stale ?? 0).toLocaleString("en-IN")}</div>
          <div className="s">silent &gt; {winMin} min</div>
        </div>
        <div>
          <div className="k">App versions</div>
          <div className="v num">{d?.app_versions ?? 0}</div>
          <div className="s">distinct firmware in field</div>
        </div>
        <div>
          <div className="k">Freshest report</div>
          <div className="v num" style={{ fontSize: 18 }}>{ago(d?.freshest ?? null)}</div>
          <div className="s">most recent beacon</div>
        </div>
      </div>

      <div className="section-label">Firmware / app version - devices in field</div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">Devices by app version</div>
          <Donut
            slices={slices.map((s, i) => ({
              label: s.fw_ver ? s.fw_ver : "unknown",
              value: s.count,
              color: s.fw_ver ? VER_COLORS[i % VER_COLORS.length] : "var(--warn)",
              display: `${s.count.toLocaleString("en-IN")} dev`,
            }))}
          />
        </div>
      </div>

      {d && d.cross_company && d.by_company.length > 0 && (
        <>
          <div className="section-label">By company</div>
          <div className="tblwrap">
            <table className="tbl">
              <thead><tr><th>Company</th><th>Company ID</th><th style={{ textAlign: "right" }}>Reporting</th></tr></thead>
              <tbody>
                {d.by_company.map((c) => (
                  <tr key={c.company_id}>
                    <td>{c.name ?? <span style={{ color: "var(--t-dim)" }}>unallocated</span>}</td>
                    <td className="mono muted">{c.company_id ?? "—"}</td>
                    <td className="num" style={{ textAlign: "right" }}>{c.count.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
